/**
 * MANTENIMIENTO → PREVENTIVOS (FASE 8.3)
 *
 * Un plan preventivo NO es un parte: es la regla que, al materializarse,
 * EMITE partes PREVENTIVO (copias independientes). Pantallas:
 *   · Preventivos: listado con filtros y alta (el plan nace en BORRADOR).
 *   · Ficha: definición, técnicos, excepciones, vista previa, materializar
 *     (con previsualización y confirmación explícita), PT generados e
 *     historial.
 *
 * La vista previa NO crea nada; sólo «Materializar» crea partes, y el
 * servidor es idempotente (UNIQUE plan+ocurrencia). La UI decide qué
 * botones ofrecer; la API y la base (0060) son la barrera.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type AlertaMnt,
  type EventoPp,
  type OpcionesActivos,
  type ActivoResumen,
  type ParteResumen,
  type GeneracionPreventivos,
  type PlanPreventivoDetalle,
  type PlanPreventivoResumen,
  type ProyeccionPlan,
  type ResultadoMaterializacion,
  type TecnicoElegible,
} from '../api.ts';
import { BarraAcciones, ConfirmDialog, EmptyState, Error as ErrorCaja, Estado, fecha, fechaHora, FiltrosResponsive, Modal, Panel, SinResultados, useDatos, Vacio } from '../componentes/base.tsx';
import { ESTADOS_PLAN_PREVENTIVO, estadoPlanPreventivo } from '../estados.ts';
import { EnlaceVolver, EstadoPtEtiqueta, PrioridadEtiqueta, programacion } from '../componentes/mantenimiento.tsx';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useToast } from '../componentes/toast.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

const MIGAS = [{ texto: 'Mantenimiento' }, { texto: 'Preventivos', a: '/mantenimiento/preventivos' }];
/** UX-1C — feedback ligero de cada cambio de estado del plan. */
const EXITO_PLAN: Readonly<Record<string, string>> = { VIGENTE: 'Plan activado.', PAUSADO: 'Plan pausado.', CERRADO: 'Plan cerrado.' };

//  UX-1C: del mapa central (`estados.ts`); se conserva la forma { etiqueta, variante = tono }.
export const ESTADOS_PLAN: Readonly<Record<string, { etiqueta: string; variante: string }>> = Object.fromEntries(
  Object.entries(ESTADOS_PLAN_PREVENTIVO).map(([k, x]) => [k, { etiqueta: x.label, variante: x.tone }]),
);
export function EstadoPlanEtiqueta({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoPlanPreventivo(estado)} />;
}

/** Preajustes (mismas RRULE canónicas del motor de FASE 6 que el servidor). */
export const FRECUENCIAS: Readonly<Record<string, { etiqueta: string; rrule: string }>> = {
  DIARIA: { etiqueta: 'Diaria', rrule: 'FREQ=DAILY' },
  SEMANAL: { etiqueta: 'Semanal', rrule: 'FREQ=WEEKLY' },
  MENSUAL: { etiqueta: 'Mensual', rrule: 'FREQ=MONTHLY' },
  TRIMESTRAL: { etiqueta: 'Trimestral', rrule: 'FREQ=MONTHLY;INTERVAL=3' },
  SEMESTRAL: { etiqueta: 'Semestral', rrule: 'FREQ=MONTHLY;INTERVAL=6' },
  ANUAL: { etiqueta: 'Anual', rrule: 'FREQ=YEARLY' },
};
const POLITICAS: Readonly<Record<string, string>> = {
  EJECUTAR: 'Hacerlo igualmente',
  DESPLAZAR: 'Pasarlo al siguiente día operativo',
  DESPLAZAR_ANTERIOR: 'Adelantarlo al día operativo anterior',
  SALTAR: 'Saltar esa ocurrencia',
};
const ESTADOS_OCURRENCIA: Readonly<Record<string, { etiqueta: string; variante: string }>> = {
  NUEVA: { etiqueta: 'Nueva', variante: 'info' },
  MATERIALIZADA: { etiqueta: 'Ya materializada', variante: 'exito-suave' },
  OMITIDA: { etiqueta: 'Omitida', variante: 'neutra' },
  SIN_DIA_OPERATIVO: { etiqueta: 'Sin día operativo', variante: 'aviso' },
  BLOQUEADA: { etiqueta: 'Bloqueada', variante: 'error' },
};
const SEVERIDAD: Readonly<Record<string, string>> = { BLOQUEANTE: 'error', AVISO: 'aviso', INFO: 'neutra' };
const PRIORIDADES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'] as const;
const etiquetaPrioridad = (p: string): string => (p === 'CRITICA' ? 'Crítica' : p.charAt(0) + p.slice(1).toLowerCase());

export const hoyIso = (): string => {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
export function sumarDiasIso(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}

function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v === null || v === '' ? '—' : v}</div>
    </div>
  );
}

export function Alertas({ alertas }: { alertas: readonly AlertaMnt[] }): ReactNode {
  if (alertas.length === 0) return null;
  return (
    <ul className="lista-alertas-mnt">
      {alertas.map((a, k) => (
        <li key={k}><span className={`etiqueta ${SEVERIDAD[a.severidad] ?? 'neutra'}`}>{a.severidad === 'BLOQUEANTE' ? 'Bloqueante' : a.severidad === 'AVISO' ? 'Aviso' : 'Info'}</span> {a.mensaje}</li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- listado

const DEFAULTS = { q: '', estado: '', centro: '' } as const;

export function PaginaPreventivos(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const lista = useListaUrl(DEFAULTS);
  const [altaAbierta, setAltaAbierta] = useState(false);
  const v = (k: keyof typeof DEFAULTS) => lista.valor(k);
  const opciones = useDatos(() => api.get<OpcionesActivos>('/api/activos/opciones'), []);
  const planes = useDatos(() => {
    const p = new URLSearchParams();
    if (v('q').trim() !== '') p.set('texto', v('q').trim());
    if (v('estado') !== '') p.set('estado', v('estado'));
    if (v('centro') !== '') p.set('idCentro', v('centro'));
    return api.get<{ filas: PlanPreventivoResumen[] }>(`/api/mantenimiento/preventivos?${p.toString()}`);
  }, [v('q'), v('estado'), v('centro')]);
  const contador = (['estado', 'centro'] as const).filter((k) => v(k) !== '').length;

  return (
    <Shell
      migas={[{ texto: 'Mantenimiento' }, { texto: 'Preventivos' }]}
      titulo="Preventivos"
      acciones={puede('preventivos:crear') ? <button className="principal" onClick={() => setAltaAbierta(true)}>Nuevo plan</button> : undefined}
    >
      <div className="panel">
        <div className="panel-cuerpo">
          <FiltrosResponsive
            contador={contador}
            busqueda={<input type="search" aria-label="Buscar por código o nombre" placeholder="Buscar PP o nombre…" value={v('q')} onChange={(ev) => lista.fijar('q', ev.target.value)} />}
          >
            <div className="filtros-rejilla">
              <label className="campo"><span>Estado</span>
                <select value={v('estado')} onChange={(ev) => lista.fijar('estado', ev.target.value)}>
                  <option value="">Vivos (sin cerrados)</option>
                  {Object.entries(ESTADOS_PLAN).map(([k, e]) => <option key={k} value={k}>{e.etiqueta}</option>)}
                  <option value="TODOS">Todos</option>
                </select>
              </label>
              <label className="campo"><span>Centro</span>
                <select value={v('centro')} onChange={(ev) => lista.fijar('centro', ev.target.value)}>
                  <option value="">Todos</option>
                  {(opciones.datos?.centros ?? []).map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
                </select>
              </label>
              {contador > 0 && <button type="button" onClick={() => lista.limpiar()}>Limpiar filtros</button>}
            </div>
          </FiltrosResponsive>
        </div>
      </div>

      <NotaGeneracion />
      <div className="panel">
        <Panel estado={planes}>
          {(d) => (d.filas.length === 0 ? (
            lista.esPorDefecto
              ? <EmptyState titulo="Aún no hay planes preventivos" descripcion="Un plan preventivo define un trabajo que se repite; de él se generan los partes de trabajo." />
              : <SinResultados limpiar={lista.limpiar} />
          ) : (
            <div className="tabla-desliza">
              <table className="tabla-tarjetas" aria-label="Planes preventivos">
                <thead><tr><th>Código</th><th>Centro</th><th>Activo</th><th>Frecuencia</th><th>Próxima ejecución</th><th>Duración</th><th>Estado</th><th>Técnicos</th><th>Última ejecución</th></tr></thead>
                <tbody>
                  {d.filas.map((p) => (
                    <tr key={p.id}>
                      {/* UX-1D: el código en mono; el nombre, en la fuente de lectura. */}
                      <td className="celda-titulo"><Link className="mono" to={`/mantenimiento/preventivos/${p.id}`}>{p.codigo}</Link><div className="pequena celda-subtitulo">{p.nombre}</div></td>
                      <td data-label="Centro">{p.centro.nombre}</td>
                      <td data-label="Activo">{p.activo === null ? <span className="suave">General del centro</span> : p.activo.nombre}</td>
                      <td data-label="Frecuencia">{p.frecuencia} · {p.horaProgramada}</td>
                      <td data-label="Próxima ejecución"><ProximaEjecucion plan={p} /></td>
                      <td data-label="Duración">{String(p.duracionEstimadaMin)} min</td>
                      <td data-label="Estado"><EstadoPlanEtiqueta estado={p.estado} /></td>
                      <td data-label="Técnicos">{p.tecnicos.map((t) => t.nombre).join(', ') || <span className="suave">Sin técnico</span>}</td>
                      <td data-label="Última ejecución">{p.ultimaEjecucion == null ? <span className="suave">Todavía ninguna</span> : fecha(p.ultimaEjecucion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>

      {altaAbierta && opciones.datos !== null && (
        <FormPlan opciones={opciones.datos} cerrar={() => setAltaAbierta(false)} alGuardar={(id) => navegar(`/mantenimiento/preventivos/${id}`)} />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- alta / edición

function FormPlan({
  opciones,
  plan,
  cerrar,
  alGuardar,
}: {
  opciones?: OpcionesActivos;
  plan?: PlanPreventivoDetalle;
  cerrar: () => void;
  alGuardar: (id: string) => void;
}): ReactNode {
  const edita = plan !== undefined;
  const centros = opciones?.centros ?? [];
  const frecInicial = plan === undefined ? 'MENSUAL' : (Object.entries(FRECUENCIAS).find(([, f]) => f.rrule === plan.rrule)?.[0] ?? '');
  const [idCentro, setIdCentro] = useState(plan?.centro.id ?? (centros.length === 1 ? centros[0]!.id : ''));
  const [idActivo, setIdActivo] = useState(plan?.activo?.id ?? '');
  const [nombre, setNombre] = useState(plan?.nombre ?? '');
  const [tipoTrabajo, setTipoTrabajo] = useState(plan?.tipoTrabajo ?? '');
  const [descripcion, setDescripcion] = useState(plan?.descripcion ?? '');
  const [prioridad, setPrioridad] = useState<string>(plan?.prioridad ?? 'MEDIA');
  const [frecuencia, setFrecuencia] = useState(frecInicial);
  const [fechaInicio, setFechaInicio] = useState(plan?.fechaInicio ?? hoyIso());
  const [fechaFin, setFechaFin] = useState(plan?.fechaFin ?? '');
  const [hora, setHora] = useState(plan?.horaProgramada ?? '09:00');
  const [duracion, setDuracion] = useState(String(plan?.duracionEstimadaMin ?? 60));
  const [politica, setPolitica] = useState<string>(plan?.politicaFestivo ?? 'DESPLAZAR');
  const [idsTecnicos, setIdsTecnicos] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);

  const activos = useDatos(
    () => (idCentro === '' ? Promise.resolve({ filas: [] as ActivoResumen[], total: 0 }) : api.get<{ filas: ActivoResumen[]; total: number }>(`/api/activos?titularidad=CLIENTE&idCentro=${idCentro}&porPagina=200`)),
    [idCentro],
  );
  const tecnicos = useDatos(
    () => (idCentro === '' || edita ? Promise.resolve({ filas: [] as TecnicoElegible[] }) : api.get<{ filas: TecnicoElegible[] }>(`/api/mantenimiento/tecnicos?idCentro=${idCentro}`)),
    [idCentro, edita],
  );

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    const cuerpo: Record<string, unknown> = {
      idActivo: idActivo === '' ? null : idActivo, nombre, tipoTrabajo, descripcion: descripcion.trim() || null, prioridad,
      fechaInicio, fechaFin: fechaFin === '' ? null : fechaFin, horaProgramada: hora, duracionEstimadaMin: Number(duracion), politicaFestivo: politica,
      //  Una regla no estándar se conserva mientras no se elija un preajuste.
      ...(frecuencia !== '' ? { frecuencia } : {}),
    };
    try {
      const r = edita
        ? await api.patch<PlanPreventivoDetalle>(`/api/mantenimiento/preventivos/${plan.id}`, { ...cuerpo, versionEsperada: plan.actualizadoEn })
        : await api.post<PlanPreventivoDetalle>('/api/mantenimiento/preventivos', { ...cuerpo, idCentro, idsTecnicos });
      alGuardar(r.id);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  const titulo = edita ? `Editar ${plan.codigo}` : 'Nuevo plan preventivo';
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label={titulo}>
        {edita && (
          //  F12.3: con la generación automática hay partes ya creados para los próximos días: decirlo claro.
          <div className="nota">
            Los cambios valen desde ahora para las ocurrencias que todavía no tienen parte
            {plan.generadosHasta != null ? <> (las posteriores al <strong>{fecha(plan.generadosHasta)}</strong>)</> : null}.
            {' '}Los partes ya generados no se reescriben: si alguno debe cambiar, ajústalo en su ficha.
          </div>
        )}
        {!edita && (
          <div className="campo">
            <label htmlFor="pp-centro">Centro</label>
            <select id="pp-centro" value={idCentro} required onChange={(ev) => { setIdCentro(ev.target.value); setIdActivo(''); setIdsTecnicos([]); }}>
              <option value="">Elige un centro…</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.cliente.nombre} — {c.nombre}</option>)}
            </select>
            {campos['idCentro'] !== undefined && <div className="error-campo">{campos['idCentro']}</div>}
          </div>
        )}
        <div className="campo">
          <label htmlFor="pp-activo">Activo</label>
          <select id="pp-activo" value={idActivo} disabled={idCentro === ''} onChange={(ev) => setIdActivo(ev.target.value)}>
            <option value="">General del centro (sin activo concreto)</option>
            {(activos.datos?.filas ?? []).map((a) => <option key={a.id} value={a.id}>{a.codigo} — {a.nombre}</option>)}
          </select>
          {campos['idActivo'] !== undefined && <div className="error-campo">{campos['idActivo']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="pp-nombre">Nombre</label>
          <input id="pp-nombre" value={nombre} required onChange={(ev) => setNombre(ev.target.value)} />
          {campos['nombre'] !== undefined && <div className="error-campo">{campos['nombre']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="pp-trabajo">Trabajo</label>
          <input id="pp-trabajo" value={tipoTrabajo} required placeholder="p. ej. Revisión de filtros y gas" onChange={(ev) => setTipoTrabajo(ev.target.value)} />
          {campos['tipoTrabajo'] !== undefined && <div className="error-campo">{campos['tipoTrabajo']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="pp-descripcion">Instrucciones (opcional)</label>
          <textarea id="pp-descripcion" rows={2} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} />
        </div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo">
            <label htmlFor="pp-frecuencia">Frecuencia</label>
            <select id="pp-frecuencia" value={frecuencia} onChange={(ev) => setFrecuencia(ev.target.value)}>
              {frecuencia === '' && <option value="">{plan?.frecuencia ?? 'Personalizada'} (sin cambios)</option>}
              {Object.entries(FRECUENCIAS).map(([k, f]) => <option key={k} value={k}>{f.etiqueta}</option>)}
            </select>
            {(campos['frecuencia'] ?? campos['rrule']) !== undefined && <div className="error-campo">{campos['frecuencia'] ?? campos['rrule']}</div>}
          </div>
          <div className="campo">
            <label htmlFor="pp-prioridad">Prioridad</label>
            <select id="pp-prioridad" value={prioridad} onChange={(ev) => setPrioridad(ev.target.value)}>
              {PRIORIDADES.map((p) => <option key={p} value={p}>{etiquetaPrioridad(p)}</option>)}
            </select>
          </div>
        </div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="pp-inicio">Primera fecha</label><input id="pp-inicio" type="date" required value={fechaInicio} onChange={(ev) => setFechaInicio(ev.target.value)} />
            {campos['fechaInicio'] !== undefined && <div className="error-campo">{campos['fechaInicio']}</div>}</div>
          <div className="campo"><label htmlFor="pp-hora">Hora</label><input id="pp-hora" type="time" required value={hora} onChange={(ev) => setHora(ev.target.value)} />
            {campos['horaProgramada'] !== undefined && <div className="error-campo">{campos['horaProgramada']}</div>}</div>
          <div className="campo"><label htmlFor="pp-duracion">Duración (min)</label><input id="pp-duracion" type="number" min={1} max={1440} required value={duracion} onChange={(ev) => setDuracion(ev.target.value)} />
            {campos['duracionEstimadaMin'] !== undefined && <div className="error-campo">{campos['duracionEstimadaMin']}</div>}</div>
          <div className="campo"><label htmlFor="pp-fin">Hasta (opcional)</label><input id="pp-fin" type="date" value={fechaFin} onChange={(ev) => setFechaFin(ev.target.value)} />
            {campos['fechaFin'] !== undefined && <div className="error-campo">{campos['fechaFin']}</div>}</div>
        </div>
        <div className="campo">
          <label htmlFor="pp-politica">Si cae en festivo o día no operativo</label>
          <select id="pp-politica" value={politica} onChange={(ev) => setPolitica(ev.target.value)}>
            {Object.entries(POLITICAS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </div>
        {!edita && idCentro !== '' && (
          <fieldset className="campo">
            <legend>Técnicos (opcional)</legend>
            {(tecnicos.datos?.filas ?? []).length === 0 ? <p className="suave pequena">No hay técnicos de mantenimiento habilitados en este centro.</p>
              : (tecnicos.datos?.filas ?? []).map((t) => (
                <label key={t.idOperario} className="fila">
                  <input type="checkbox" checked={idsTecnicos.includes(t.idOperario)} onChange={() => setIdsTecnicos((l) => (l.includes(t.idOperario) ? l.filter((x) => x !== t.idOperario) : [...l, t.idOperario]))} /> {t.nombre}
                  {t.especialidadOperativa === 'AMBAS' && <span className="suave pequena"> (limpieza y mantenimiento)</span>}
                </label>
              ))}
          </fieldset>
        )}
        {!edita && <p className="suave pequena">El plan se crea en borrador: verás la vista previa de sus fechas en la ficha antes de activarlo. No genera ningún parte hasta que lo actives y lo materialices.</p>}
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando}>{enviando ? 'Guardando…' : edita ? 'Guardar cambios' : 'Crear en borrador'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- ficha

type Dialogo = 'editar' | 'materializar' | 'cerrar' | null;

export function PaginaPreventivo(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const base = `/api/mantenimiento/preventivos/${encodeURIComponent(id)}`;
  const plan = useDatos(() => api.get<PlanPreventivoDetalle>(base), [id]);
  const historial = useHistorial<EventoPp>(`${base}/historial`);
  const partes = useDatos(() => api.get<{ filas: ParteResumen[]; total: number }>(`${base}/partes`), [id]);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<ReactNode>(null);
  const toast = useToast();
  const [rango, setRango] = useState({ desde: hoyIso(), hasta: sumarDiasIso(hoyIso(), 90) });
  const [version, setVersion] = useState(0);
  const proyeccion = useDatos(
    () => api.get<ProyeccionPlan>(`${base}/proyeccion?desde=${rango.desde}&hasta=${rango.hasta}`),
    [id, rango.desde, rango.hasta, version],
  );

  function recargar(): void {
    setDialogo(null);
    plan.recargar();
    historial.recargar();
    partes.recargar();
    setVersion((n) => n + 1);
  }
  /** UX-1C: `exito` → toast ligero; los errores siguen en la página. */
  async function accion(f: () => Promise<unknown>, exito?: string): Promise<void> {
    setError(null);
    setAviso(null);
    try {
      await f();
      recargar();
      if (exito !== undefined) toast.exito(exito);
    } catch (e) {
      setError(e);
    }
  }

  if (plan.error instanceof FalloApi && (plan.error.estado === 404 || plan.error.estado === 403)) {
    return (
      <Shell migas={MIGAS} titulo="Plan no disponible">
        <div className="panel"><Vacio titulo="Este plan no existe o no tienes acceso"><Link to="/mantenimiento/preventivos">Volver</Link></Vacio></div>
      </Shell>
    );
  }
  const p = plan.datos;
  const gestiona = puede('preventivos:gestionar');
  const estado = (e: string) => accion(() => api.post(`${base}/estado`, { estado: e }), EXITO_PLAN[e]);

  return (
    <Shell
      migas={[...MIGAS, { texto: p?.codigo ?? '…' }]}
      titulo={p === null ? 'Plan preventivo' : <><span className="mono">{p.codigo}</span> · {p.nombre}</>}
      estado={p === null ? undefined : <EstadoPlanEtiqueta estado={p.estado} />}
      meta={p === null ? undefined : (
        <><PrioridadEtiqueta prioridad={p.prioridad} /><span>{p.centro.nombre}</span>
          {p.activo !== null && <Link className="mono" to={`/activos/${p.activo.id}`}>{p.activo.codigo}</Link>}
          <span>{p.frecuencia} · {p.horaProgramada}</span>
          {p.proximaFecha !== null && <span>Próxima: <ProximaEjecucion plan={p} /></span>}</>
      )}
      acciones={p === null || !gestiona || p.estado === 'CERRADO' ? undefined : (
        //  UX-1B: la principal sigue el estado (activar → materializar / reactivar); Editar, secundaria;
        //  pausar y cerrar (irreversible, con confirmación) en «Más acciones».
        <BarraAcciones etiqueta="Acciones del plan"
          //  F12.3: un plan vigente se genera solo; «Generar partes de un rango» queda para adelantar o recuperar.
          principal={p.estado === 'BORRADOR' ? <button className="principal" onClick={() => void estado('VIGENTE')}>Activar</button>
            : p.estado === 'PAUSADO' ? <button className="principal" onClick={() => void estado('VIGENTE')}>Reactivar</button>
            : <button className="principal" onClick={() => setDialogo('editar')}>Editar</button>}
          secundaria={p.estado === 'VIGENTE' ? undefined : <button onClick={() => setDialogo('editar')}>Editar</button>}
          mas={[
            p.estado === 'VIGENTE' && puede('preventivos:materializar') && { texto: 'Generar partes de un rango', alPulsar: () => setDialogo('materializar') },
            p.estado === 'VIGENTE' && { texto: 'Pausar', alPulsar: () => void estado('PAUSADO') },
            { texto: 'Cerrar plan', alPulsar: () => setDialogo('cerrar'), peligro: true },
          ]} />
      )}
    >
     {/* F12.3: en móvil, todo lo que se toca en la ficha del plan llega a 44px (técnicos, excepciones, vista previa). */}
     <div className="ficha-pp">
      {/* F12.4: llegando desde Indicadores (ocurrencias perdidas), la vuelta al mismo sitio. */}
      <EnlaceVolver />
      {error !== null && <ErrorCaja error={error} />}
      {aviso !== null && <div className="nota exito" role="status">{aviso}</div>}

      <Panel estado={plan}>
        {(d) => (
          <>
            {d.estado === 'VIGENTE' && <NotaGeneracion plan={d} />}
            {(d.ocurrenciasNoGeneradas ?? []).length > 0 && (
              <div className="nota aviso" role="status">
                <strong>Ocurrencias pasadas sin parte:</strong> {(d.ocurrenciasNoGeneradas ?? []).map((f) => fecha(f)).join(', ')}.
                {' '}No se generan hacia atrás: si ese mantenimiento sigue haciendo falta, crea el parte desde el activo o el listado de partes.
              </div>
            )}
            {d.estado === 'PAUSADO' && <div className="nota aviso" role="status"><strong>Pausado.</strong> No genera partes hasta reactivarlo; los ya emitidos siguen su curso.</div>}
            {d.estado === 'CERRADO' && <div className="nota" role="status"><strong>Cerrado.</strong> Ya no genera nada; el histórico y sus partes se conservan.</div>}
            <div className="panel">
              <div className="panel-cabecera"><h2>Definición</h2></div>
              <div className="panel-cuerpo datos">
                <Dato e="Cliente" v={d.cliente.nombre} />
                <Dato e="Centro" v={d.centro.nombre} />
                <Dato e="Objetivo" v={d.activo === null ? 'General del centro' : <Link to={`/activos/${d.activo.id}`}>{d.activo.codigo} — {d.activo.nombre}</Link>} />
                <Dato e="Trabajo" v={d.tipoTrabajo} />
                <Dato e="Frecuencia" v={`${d.frecuencia} · ${d.horaProgramada} (${d.zonaHoraria})`} />
                <Dato e="Vigencia" v={`Desde ${fecha(d.fechaInicio)}${d.fechaFin === null ? '' : ` hasta ${fecha(d.fechaFin)}`}`} />
                <Dato e="Duración" v={`${String(d.duracionEstimadaMin)} min`} />
                <Dato e="Festivos" v={POLITICAS[d.politicaFestivo] ?? d.politicaFestivo} />
                <Dato e="Próxima ejecución" v={d.proximaFecha === null ? null : <ProximaEjecucion plan={d} />} />
                <Dato e="Última ejecución" v={d.ultimaEjecucion == null ? 'Todavía ninguna' : fecha(d.ultimaEjecucion)} />
                <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
              </div>
              {d.descripcion !== null && <div className="panel-cuerpo"><p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{d.descripcion}</p></div>}
            </div>
            <SeccionTecnicosPlan plan={d} gestiona={gestiona} accion={accion} />
            <SeccionExcepciones plan={d} gestiona={gestiona} accion={accion} />
          </>
        )}
      </Panel>

      <section className="panel" aria-label="Vista previa">
        <div className="panel-cabecera"><h2>Vista previa</h2></div>
        <div className="panel-cuerpo">
          <p className="suave pequena">Fechas que generaría el plan. La vista previa no crea partes.</p>
          <div className="fila" style={{ flexWrap: 'wrap' }}>
            <label className="campo"><span>Desde</span><input type="date" value={rango.desde} onChange={(ev) => setRango((r) => ({ ...r, desde: ev.target.value }))} /></label>
            <label className="campo"><span>Hasta</span><input type="date" value={rango.hasta} onChange={(ev) => setRango((r) => ({ ...r, hasta: ev.target.value }))} /></label>
          </div>
          <Panel estado={proyeccion}>{(pr) => <TablaProyeccion proyeccion={pr} />}</Panel>
        </div>
      </section>

      <section className="panel" aria-label="Partes generados">
        <div className="panel-cabecera"><h2>Partes generados</h2></div>
        <div className="panel-cuerpo">
          <Panel estado={partes}>
            {/* FASE 9.5-B: tabla (tarjetas en móvil) con lo que hace falta para ir directo al parte. */}
            {(l) => (l.filas.length === 0 ? <EmptyState titulo="Este plan aún no ha generado partes." descripcion="Se generan al materializar el plan cuando está vigente." /> : (
              <table className="tabla-tarjetas" aria-label="Partes generados por el plan">
                <thead><tr><th>Fecha</th><th>Parte</th><th>Estado</th><th>Activo</th><th>Técnicos</th></tr></thead>
                <tbody>
                  {l.filas.map((x) => (
                    <tr key={x.id}>
                      <td data-label="Fecha">{programacion(x.fechaProgramada, x.horaProgramada)}
                        {x.fechaOcurrencia !== null && x.fechaOcurrencia !== x.fechaProgramada && <div className="suave pequena">ocurrencia del {fecha(x.fechaOcurrencia)}</div>}</td>
                      <td data-label="Parte"><Link className="mono" to={`/mantenimiento/partes/${x.id}`}>{x.codigo}</Link></td>
                      <td data-label="Estado"><EstadoPtEtiqueta estado={x.estado} /></td>
                      <td data-label="Activo">{x.activos.length === 0 ? '—' : x.activos.map((a) => <div key={a.id}><Link className="mono" to={`/activos/${a.id}`}>{a.codigo}</Link> {a.nombre}</div>)}</td>
                      <td data-label="Técnicos">{x.tecnicos.length === 0 ? '—' : x.tecnicos.map((t) => t.nombre).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </Panel>
        </div>
      </section>

      <section className="panel" aria-label="Historial">
        <div className="panel-cabecera"><h2>Historial</h2></div>
        <div className="panel-cuerpo">
          <Panel estado={historial}>{(h) => <HistorialPlan eventos={h.filas} />}</Panel>
          <PieHistorial historial={historial} />
        </div>
      </section>

      {p !== null && dialogo === 'editar' && <FormPlan plan={p} cerrar={() => setDialogo(null)} alGuardar={() => { recargar(); toast.exito('Cambios guardados.'); }} />}
      {p !== null && dialogo === 'cerrar' && (
        <ConfirmDialog titulo={`Cerrar ${p.codigo}`} confirmar="Cerrar plan" cerrar={() => setDialogo(null)}
          descripcion={<p>{p.nombre}</p>}
          consecuencia="El plan dejará de generar partes para siempre. Los partes ya emitidos y el historial se conservan."
          alConfirmar={() => void estado('CERRADO')} />
      )}
      {p !== null && dialogo === 'materializar' && (
        <DialogoMaterializar
          plan={p}
          cerrar={() => setDialogo(null)}
          hecho={(r) => {
            recargar();
            setAviso(r.creados.length === 0
              ? 'No había ocurrencias nuevas que generar en ese rango.'
              : `Generados ${String(r.creados.length)} partes: ${r.creados.map((c) => c.codigo).join(', ')}.`);
          }}
        />
      )}
     </div>
    </Shell>
  );
}

/**
 * FASE 9.1-B: la vista previa MUESTRA 5 ocurrencias y «Mostrar más» de 10 en
 * 10. Es sólo presentación: el servidor calcula el rango entero (el resumen
 * cuenta todas) y la materialización nunca se trunca por esto.
 */
export const PREVIA_INICIAL = 5;

/** F12.3 — la próxima ejecución REAL; si un festivo la movió, también la que le tocaba. */
function ProximaEjecucion({ plan: p }: { plan: PlanPreventivoResumen }): ReactNode {
  if (p.proximaFecha === null) return <span className="suave">—</span>;
  return (
    <>
      {fecha(p.proximaFecha)}
      {p.proximaFechaOriginal != null && <span className="suave pequena"> (le tocaba el {fecha(p.proximaFechaOriginal)}: desplazada por festivo)</span>}
    </>
  );
}

/** F12.3 — que nadie piense que hay que pulsar algo para que el preventivo ocurra. */
function NotaGeneracion({ plan }: { plan?: PlanPreventivoDetalle }): ReactNode {
  const g = useDatos(() => api.get<GeneracionPreventivos>('/api/mantenimiento/preventivos/generacion'), []);
  const h = g.datos?.horizonteDias ?? 30;
  return (
    <div className="nota info" role="status">
      <strong>Los preventivos se generan automáticamente:</strong> cada plan vigente tiene ya su parte para los próximos {h} días.
      {plan?.generadosHasta != null && <> Este plan tiene partes hasta el {fecha(plan.generadosHasta)}.</>}
      {g.datos?.ultimaGeneracion != null && <span className="suave pequena"> Última generación: {fechaHora(g.datos.ultimaGeneracion)}.</span>}
    </div>
  );
}

function TablaProyeccion({ proyeccion: pr }: { proyeccion: ProyeccionPlan }): ReactNode {
  const [visibles, setVisibles] = useState(PREVIA_INICIAL);
  if (pr.nota !== null && pr.ocurrencias.length === 0) return <p className="nota">{pr.nota}</p>;
  return (
    <>
      {pr.nota !== null && <p className="nota">{pr.nota}</p>}
      {pr.tecnicosPrevistos.some((t) => !t.valido) && (
        <div className="nota aviso">{pr.tecnicosPrevistos.filter((t) => !t.valido).map((t) => <div key={t.idOperario}>{t.nombre}: {t.motivo}</div>)}</div>
      )}
      <p className="pequena">
        {pr.resumen.total} ocurrencias · {pr.resumen.nuevas} nuevas · {pr.resumen.materializadas} ya materializadas
        {pr.resumen.omitidas > 0 && ` · ${String(pr.resumen.omitidas)} omitidas`}
        {pr.resumen.conConflictos > 0 && ` · ${String(pr.resumen.conConflictos)} con conflictos`}
      </p>
      {pr.ocurrencias.length === 0 ? <p className="suave">No hay ocurrencias en ese rango.</p> : (
        <div className="tabla-desliza">
          <table aria-label="Ocurrencias">
            <thead><tr><th>Fecha</th><th>Estado</th><th>Técnicos</th><th>Alertas</th></tr></thead>
            <tbody>
              {pr.ocurrencias.slice(0, visibles).map((o) => (
                <tr key={o.fechaOcurrencia}>
                  <td>{programacion(o.fecha, o.hora)}{o.fecha !== o.fechaOcurrencia && <div className="suave pequena">(prevista el {fecha(o.fechaOcurrencia)})</div>}</td>
                  <td>
                    <span className={`etiqueta ${ESTADOS_OCURRENCIA[o.estado]?.variante ?? 'neutra'}`}>{ESTADOS_OCURRENCIA[o.estado]?.etiqueta ?? o.estado}</span>
                    {o.parte !== null && <div><Link className="mono" to={`/mantenimiento/partes/${o.parte.id}`}>{o.parte.codigo}</Link></div>}
                  </td>
                  <td>{o.tecnicos.map((t) => t.nombre).join(', ') || <span className="suave">Sin técnico</span>}</td>
                  <td><Alertas alertas={o.alertas} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {pr.ocurrencias.length > visibles && (
            <div className="pie-historial fila" role="group" aria-label="Más ocurrencias">
              <span className="suave pequena">Mostrando {visibles} de {pr.ocurrencias.length} ocurrencias calculadas (sólo la vista; el cálculo es completo).</span>
              <button type="button" className="pequeno" onClick={() => setVisibles((v) => v + 10)}>Mostrar más</button>
              <button type="button" className="pequeno" onClick={() => setVisibles(pr.ocurrencias.length)}>Ver todas</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** Previsualiza el rango y sólo crea al confirmar. Rango máx. 92 días, nunca en el pasado (el servidor lo exige). */
function DialogoMaterializar({ plan, cerrar, hecho }: { plan: PlanPreventivoDetalle; cerrar: () => void; hecho: (r: ResultadoMaterializacion) => void }): ReactNode {
  const [desde, setDesde] = useState(hoyIso());
  const [hasta, setHasta] = useState(sumarDiasIso(hoyIso(), 30));
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const base = `/api/mantenimiento/preventivos/${plan.id}`;
  const previa = useDatos(() => api.get<ProyeccionPlan>(`${base}/proyeccion?desde=${desde}&hasta=${hasta}`), [desde, hasta]);

  async function confirmar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      hecho(await api.post<ResultadoMaterializacion>(`${base}/materializar`, { desde, hasta }));
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  const nuevas = previa.datos?.resumen.nuevas ?? 0;
  return (
    <Modal titulo={`Generar partes de ${plan.codigo}`} cerrar={cerrar}>
      <p>Normalmente no hace falta: <strong>los preventivos se generan automáticamente</strong>. Úsalo para adelantar un rango
        más allá del habitual o para recuperar. Se crean partes reales para las ocurrencias nuevas del rango; repetirlo no duplica nada.</p>
      <div className="fila" style={{ flexWrap: 'wrap' }}>
        <label className="campo"><span>Desde</span><input type="date" value={desde} min={hoyIso()} onChange={(ev) => setDesde(ev.target.value)} /></label>
        <label className="campo"><span>Hasta</span><input type="date" value={hasta} onChange={(ev) => setHasta(ev.target.value)} /></label>
      </div>
      <Panel estado={previa}>
        {(pr) => (
          <div aria-label="Resumen de materialización">
            <p><strong>{pr.resumen.nuevas} nuevas</strong> · {pr.resumen.materializadas} ya materializadas · {pr.resumen.conConflictos} con conflictos
              {pr.resumen.omitidas + pr.resumen.bloqueadas > 0 && ` · ${String(pr.resumen.omitidas + pr.resumen.bloqueadas)} no se generan`}</p>
            {pr.ocurrencias.filter((o) => o.estado === 'NUEVA' && o.alertas.some((a) => a.severidad !== 'INFO')).map((o) => (
              <div key={o.fechaOcurrencia} className="nota aviso"><strong>{programacion(o.fecha, o.hora)}</strong><Alertas alertas={o.alertas.filter((a) => a.severidad !== 'INFO')} /></div>
            ))}
          </div>
        )}
      </Panel>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila">
        <button className="principal" disabled={enviando || nuevas === 0} onClick={() => void confirmar()}>
          {enviando ? 'Generando…' : `Generar ${String(nuevas)} ${nuevas === 1 ? 'parte' : 'partes'}`}
        </button>
        <button onClick={cerrar}>Cancelar</button>
      </div>
    </Modal>
  );
}

function SeccionTecnicosPlan({ plan: p, gestiona, accion }: { plan: PlanPreventivoDetalle; gestiona: boolean; accion: (f: () => Promise<unknown>) => Promise<void> }): ReactNode {
  const editable = gestiona && p.estado !== 'CERRADO';
  const elegibles = useDatos(
    () => (editable ? api.get<{ filas: TecnicoElegible[] }>(`/api/mantenimiento/tecnicos?idCentro=${p.centro.id}`) : Promise.resolve({ filas: [] as TecnicoElegible[] })),
    [editable, p.centro.id],
  );
  const [elegido, setElegido] = useState('');
  const libres = (elegibles.datos?.filas ?? []).filter((t) => !p.tecnicos.some((x) => x.idOperario === t.idOperario));
  const base = `/api/mantenimiento/preventivos/${p.id}`;
  return (
    <section className="panel" aria-label="Técnicos del plan">
      <div className="panel-cabecera"><h2>Técnicos</h2></div>
      <div className="panel-cuerpo">
        <p className="suave pequena">Se copian a cada parte al materializar. Sin técnicos, el parte nace ABIERTO para asignarlo después.</p>
        {p.tecnicos.length === 0 ? <p className="suave">Sin técnicos.</p> : (
          <ul>
            {p.tecnicos.map((t) => (
              <li key={t.idOperario} className="fila">
                {t.nombre}
                {editable && <button className="pequeno" onClick={() => void accion(() => api.post(`${base}/tecnicos/${t.idOperario}/retirar`))}>Quitar</button>}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <div className="fila" style={{ flexWrap: 'wrap' }}>
            <label className="campo"><span>Añadir técnico</span>
              <select value={elegido} onChange={(ev) => setElegido(ev.target.value)}>
                <option value="">Elige…</option>
                {libres.map((t) => <option key={t.idOperario} value={t.idOperario}>{t.nombre}{t.especialidadOperativa === 'AMBAS' ? ' (limpieza y mantenimiento)' : ''}</option>)}
              </select>
            </label>
            <button disabled={elegido === ''} onClick={() => { void accion(() => api.post(`${base}/tecnicos`, { idOperario: elegido })); setElegido(''); }}>Añadir</button>
          </div>
        )}
      </div>
    </section>
  );
}

function SeccionExcepciones({ plan: p, gestiona, accion }: { plan: PlanPreventivoDetalle; gestiona: boolean; accion: (f: () => Promise<unknown>) => Promise<void> }): ReactNode {
  const editable = gestiona && p.estado !== 'CERRADO';
  const [f, setF] = useState('');
  const [tipo, setTipo] = useState<'EXCLUIR' | 'INCLUIR'>('EXCLUIR');
  const [motivo, setMotivo] = useState('');
  const base = `/api/mantenimiento/preventivos/${p.id}`;
  return (
    <section className="panel" aria-label="Excepciones">
      <div className="panel-cabecera"><h2>Excepciones</h2></div>
      <div className="panel-cuerpo">
        {p.excepciones.length === 0 ? <p className="suave">Sin excepciones: se sigue la frecuencia y el calendario del centro.</p> : (
          <ul>
            {p.excepciones.map((x) => (
              <li key={x.id} className="fila" style={{ flexWrap: 'wrap' }}>
                <span className={`etiqueta ${x.tipo === 'EXCLUIR' ? 'aviso' : 'info'}`}>{x.tipo === 'EXCLUIR' ? 'Omitir' : 'Añadir'}</span>
                {fecha(x.fecha)} <span className="suave pequena">{x.motivo}</span>
                {editable && <button className="pequeno" onClick={() => void accion(() => api.post(`${base}/excepciones/${x.id}/retirar`))}>Quitar</button>}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <form className="fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} aria-label="Nueva excepción"
            onSubmit={(ev) => { ev.preventDefault(); void accion(() => api.post(`${base}/excepciones`, { fecha: f, tipo, motivo })); setF(''); setMotivo(''); }}>
            <label className="campo"><span>Fecha</span><input type="date" required value={f} onChange={(ev) => setF(ev.target.value)} /></label>
            <label className="campo"><span>Tipo</span>
              <select value={tipo} onChange={(ev) => setTipo(ev.target.value as 'EXCLUIR' | 'INCLUIR')}>
                <option value="EXCLUIR">Omitir esa fecha</option>
                <option value="INCLUIR">Añadir esa fecha</option>
              </select>
            </label>
            <label className="campo"><span>Motivo</span><input required minLength={3} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></label>
            <button type="submit">Añadir excepción</button>
          </form>
        )}
      </div>
    </section>
  );
}

const TIPOS_EVENTO: Readonly<Record<string, string>> = {
  ALTA: 'Alta del plan',
  EDICION: 'Edición',
  ESTADO: 'Cambio de estado',
  TECNICO_ANADIDO: 'Técnico añadido',
  TECNICO_RETIRADO: 'Técnico retirado',
  EXCEPCION_ANADIDA: 'Excepción añadida',
  EXCEPCION_RETIRADA: 'Excepción retirada',
  PT_GENERADO: 'Parte generado',
};

function HistorialPlan({ eventos }: { eventos: readonly EventoPp[] }): ReactNode {
  if (eventos.length === 0) return <p className="suave">Sin eventos.</p>;
  return (
    <ol className="lista-historial">
      {eventos.map((e) => (
        <li key={e.id}>
          <strong>{TIPOS_EVENTO[e.tipo] ?? e.tipo}</strong>
          {e.estadoNuevo !== null && <> · {ESTADOS_PLAN[e.estadoAnterior ?? '']?.etiqueta ?? '—'} → {ESTADOS_PLAN[e.estadoNuevo]?.etiqueta ?? e.estadoNuevo}</>}
          {e.operario !== null && <> · {e.operario}</>}
          {e.parte !== null && e.idParte !== null && <> · <Link className="mono" to={`/mantenimiento/partes/${e.idParte}`}>{e.parte}</Link></>}
          {e.fecha !== null && <> · {fecha(e.fecha)}</>}
          <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div>
        </li>
      ))}
    </ol>
  );
}
